// Global Instructions Rule Applied!
import { Tooltip } from 'antd'
import React from 'react'

/**
 * Helper function to get initials from a name
 * @param {string} name - The full name to extract initials from
 * @returns {string} - The initials (up to 2 characters)
 */
const getContactInitials = (name) => {
  if (!name) return '??'
  const nameParts = name.trim().split(' ')
  if (nameParts.length >= 2) {
    return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

/**
 * Helper function to generate a consistent color for a contact
 * @param {string|number} contactId - The contact identifier for color generation
 * @returns {string} - A hex color code
 */
export const getContactColor = (contactId) => {
  // Fast deterministic 32-bit hash (FNV-1a)
  const fnv1a32 = (str) => {
    let h = 0x811c9dc5
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i)
      h = Math.imul(h, 0x01000193)
    }
    return h >>> 0 // unsigned
  }

  // HSL -> HEX
  const hslToHex = (h, s, l) => {
    s /= 100
    l /= 100

    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = l - c / 2

    let r = 0,
      g = 0,
      b = 0
    if (0 <= h && h < 60) [r, g, b] = [c, x, 0]
    else if (60 <= h && h < 120) [r, g, b] = [x, c, 0]
    else if (120 <= h && h < 180) [r, g, b] = [0, c, x]
    else if (180 <= h && h < 240) [r, g, b] = [0, x, c]
    else if (240 <= h && h < 300) [r, g, b] = [x, 0, c]
    else [r, g, b] = [c, 0, x]

    const toHex = (v) =>
      Math.round((v + m) * 255)
        .toString(16)
        .padStart(2, '0')

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  }

  const input = String(contactId ?? '')
    .toLowerCase()
    .trim()
  const hash = fnv1a32(input)

  // Hue across full spectrum; constrain sat/light for pleasant, readable colours
  const hue = hash % 360 // 0..359
  const sat = 55 + ((hash >>> 9) % 25) // 55..79
  const lit = 42 + ((hash >>> 17) % 18) // 42..59

  return hslToHex(hue, sat, lit)
}

/**
 * Reusable component for displaying a list of user icons with initials
 * @param {object} props
 * @param {Array<string>} [props.contactNames] - Contact names to display
 * @param {Array<string|number>} [props.contactIds] - Contact IDs for color generation
 * @param {number} [props.maxVisible] - Maximum icons before the "+{number}" indicator
 * @param {'small'|'medium'|'large'|string} [props.size] - Icon size: 'small' (16px), 'medium' (20px), 'large' (24px)
 * @param {boolean} [props.badges] - Display as badges with full names
 */
const UserIconList = ({ contactNames = [], contactIds = [], maxVisible, size = 'small', badges = false }) => {
  // Return null if no contact data provided at all
  if ((!contactNames || contactNames.length === 0) && (!contactIds || contactIds.length === 0)) {
    return null
  }

  // If we have contactIds but no contactNames, create fallback names
  const effectiveContactNames =
    contactNames && contactNames.length > 0
      ? contactNames
      : (contactIds || []).map((id, index) => {
          // Create a more user-friendly fallback
          if (typeof id === 'string' && id.length > 8) {
            // If it's a UUID, use first few characters
            return `U${id.substring(0, 2).toUpperCase()}`
          }
          return `U${id}`
        })

  // Ensure we have some names to display
  if (!effectiveContactNames || effectiveContactNames.length === 0) {
    return null
  }

  // Create pairs of names and IDs, then sort by name
  const nameIdPairs = effectiveContactNames.map((name, index) => ({
    name,
    id: contactIds && contactIds.length > index ? contactIds[index] : index
  }))
  nameIdPairs.sort((a, b) => a.name.localeCompare(b.name))

  // Extract sorted arrays
  const sortedNames = nameIdPairs.map((pair) => pair.name)
  const sortedIds = nameIdPairs.map((pair) => pair.id)

  // Determine how many to show and how many are hidden
  const totalCount = sortedNames.length
  const visibleCount = maxVisible && maxVisible > 0 ? Math.min(maxVisible, totalCount) : totalCount
  const hiddenCount = totalCount - visibleCount
  const visibleNames = sortedNames.slice(0, visibleCount)
  const hiddenNames = hiddenCount > 0 ? sortedNames.slice(visibleCount) : []
  const visibleIds = sortedIds.slice(0, visibleCount)

  // Define size configurations
  const sizeConfig = {
    small: { width: 14, height: 14, fontSize: '8px', padding: '1px' },
    medium: { width: 18, height: 18, fontSize: '10px', padding: '2px' },
    large: { width: 22, height: 22, fontSize: '11px', padding: '2px' }
  }

  const config = sizeConfig[size] || sizeConfig.small

  return (
    <div className='flex items-center space-x-0.5 flex-shrink-0'>
      {visibleNames.map((contactName, index) => {
        const contactId = visibleIds[index] || index
        if (!badges) {
          return (
            <Tooltip key={index} title={contactName}>
              <div
                className='inline-flex items-center justify-center rounded-full text-white font-bold'
                style={{
                  backgroundColor: getContactColor(contactId),
                  width: `${config.width}px`,
                  height: `${config.height}px`,
                  fontSize: config.fontSize,
                  padding: config.padding,
                  lineHeight: '1',
                  minWidth: `${config.width}px`
                }}
              >
                {getContactInitials(contactName)}
              </div>
            </Tooltip>
          )
        } else {
          return (
            <Tooltip key={index} title={contactName}>
              <div
                style={{ backgroundColor: getContactColor(contactId) }}
                className='inline-flex items-center justify-center rounded-full text-white text-xs pl-1.5 pr-1.5 pt-1 pb-1 mr-1'
              >
                {contactName}
              </div>
            </Tooltip>
          )
        }
      })}
      {hiddenCount > 0 && (
        <Tooltip
          title={
            <div className='flex flex-col gap-1'>
              {hiddenNames.map((name, idx) => (
                <div key={idx} className='text-white'>
                  {name}
                </div>
              ))}
            </div>
          }
        >
          <div
            className='inline-flex items-center justify-center rounded-full text-white font-bold bg-gray-500'
            style={{
              width: `${config.width + 3}px`,
              height: `${config.height}px`,
              fontSize: config.fontSize,
              padding: config.padding,
              lineHeight: '1',
              minWidth: `${config.width}px`
            }}
          >
            +{hiddenCount}
          </div>
        </Tooltip>
      )}
    </div>
  )
}

export default UserIconList
